import { z } from "zod";

/**
 * `MILEAGE-TOLERANCE` (founder D-1031-11, D-1101-2; the architect's ruling of 1 November 2026): **one platform setting, the mileage a
 * standing expertise report may be behind the car's odometer and still be reused.** Hakili's own setting (versioned, its own administration
 * door under `parametrerPaiements`, audited), not a bank's per-bank Politique.
 *
 * The rule the reuse decision applies once a value is published: `0 <= vehicles.mileage_km - report.odometer_read_km <= toleranceKm`, on top
 * of the report's validity (age, unchanged). Nothing published is a SERVED STATE (`unconfigured`), never a null the screen has to guess:
 * the reuse decision then answers on age alone, as it always did.
 *
 * **Codes, not words.** The console's sentences for the state and for each reuse reason are the A-H architect's; this contract serves the
 * state and the code.
 */

/** Inclusive bounds of the value, whole kilometres, served with the setting. */
export const MILEAGE_TOLERANCE_BOUNDS = { minKm: 0, maxKm: 20_000 } as const;

/**
 * Why a standing report is, or is not, reused. `age_only` and `within_tolerance` reuse; every other code books a new expertise.
 * `mileage_below_reading` (the car shows LESS than the report read, a rollback) is its own code, never folded into
 * `tolerance_exceeded` (A47: a refusal is never silent, and the two have different causes).
 */
export const REUSE_REASONS = [
  "age_only",
  "within_tolerance",
  "tolerance_exceeded",
  "mileage_below_reading",
  "reading_missing",
  "expired",
  "no_report",
] as const;
export type ReuseReason = (typeof REUSE_REASONS)[number];

function buildSchemas(object: typeof z.strictObject) {
  const version = object({
    version: z.number().int().min(1),
    toleranceKm: z.number().int().min(0).max(MILEAGE_TOLERANCE_BOUNDS.maxKm),
    effectiveFrom: z.string(),
    publishedAt: z.string(),
    publishedByName: z.string().nullable(),
  });
  const setting = object({
    /** `unconfigured` until the first publication: the reuse decision then counts age alone. */
    state: z.enum(["configured", "unconfigured"]),
    toleranceKm: z.number().int().min(0).max(MILEAGE_TOLERANCE_BOUNDS.maxKm).nullable(),
    version: z.number().int().min(1).nullable(),
    effectiveFrom: z.string().nullable(),
    publishedByName: z.string().nullable(),
    bounds: object({ minKm: z.literal(0), maxKm: z.literal(MILEAGE_TOLERANCE_BOUNDS.maxKm) }),
    /** Newest first. */
    history: z.array(version),
  });
  const answer = object({ setting });
  /** What the console reads on one car: would its standing report be reused, and if not, why (a code). */
  const verdict = object({
    vehicleId: z.string(),
    reuse: z.boolean(),
    reason: z.enum(REUSE_REASONS),
    /** The car's mileage now; the odometer the report read; their difference; the value in force (all null when not applicable). */
    currentKm: z.number().int().nullable(),
    readKm: z.number().int().nullable(),
    gapKm: z.number().int().nullable(),
    toleranceKm: z.number().int().nullable(),
    reportId: z.string().nullable(),
  });
  const verdictAnswer = object({ reuse: verdict });
  return { version, setting, answer, verdict, verdictAnswer };
}

const strict = buildSchemas(z.strictObject);
const loose = buildSchemas(z.looseObject);

export const mileageToleranceVersionSchema = strict.version;
export const mileageToleranceSettingSchema = strict.setting;
export const mileageToleranceAnswerSchema = strict.answer;
export const mileageReuseVerdictSchema = strict.verdict;
export const mileageReuseAnswerSchema = strict.verdictAnswer;
/** The READ twins: what a client parses the live answer with. */
export const mileageToleranceAnswerReadSchema = loose.answer;
export const mileageReuseAnswerReadSchema = loose.verdictAnswer;

export type MileageToleranceSetting = z.infer<typeof mileageToleranceSettingSchema>;
export type MileageToleranceAnswer = z.infer<typeof mileageToleranceAnswerSchema>;
export type MileageReuseVerdict = z.infer<typeof mileageReuseVerdictSchema>;
